(() => {
  if (window.__littleRoarInPageCartV14Loaded) return;
  window.__littleRoarInPageCartV14Loaded = true;

  const rootSelector = '[data-roar-buy-section]';
  let syncing = false;
  let pricingSyncing = false;

  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  }[character]));

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'USD'
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return ((Number(cents) || 0) / 100).toFixed(2) + ' ' + (currency || '');
    }
  };

  const ensurePricingStyles = () => {
    if (document.getElementById('little-roar-cart-pricing-v14-styles')) return;

    const style = document.createElement('style');
    style.id = 'little-roar-cart-pricing-v14-styles';
    style.textContent = `
      .little-roar-page .lr-cart-panel-footer.lr-cart-pricing-summary {
        display: flex !important;
        flex-direction: column !important;
        align-items: stretch !important;
        width: 100% !important;
        inline-size: 100% !important;
        min-width: 0 !important;
        max-width: none !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
        box-sizing: border-box !important;
        gap: 7px;
      }
      .little-roar-page .lr-cart-price-row {
        display: flex !important;
        width: 100% !important;
        inline-size: 100% !important;
        min-width: 0 !important;
        box-sizing: border-box !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 12px;
        color: rgba(23,62,57,.68);
        font-size: .74rem;
        font-weight: 850;
      }
      .little-roar-page .lr-cart-price-row strong {
        margin-left: auto !important;
        flex: 0 0 auto !important;
        color: var(--forest);
        font-size: .91rem;
        font-weight: 950;
        text-align: right;
        white-space: nowrap;
      }
      .little-roar-page .lr-cart-offer-row {
        display: flex;
        width: 100%;
        align-items: center;
        gap: 10px;
      }
      .little-roar-page .lr-cart-offer {
        width: fit-content;
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 5px 9px;
        border: 1px solid rgba(248,195,65,.42);
        border-radius: 999px;
        background: linear-gradient(135deg,rgba(255,246,206,.96),rgba(255,238,169,.82));
        color: #76540b;
        box-shadow: 0 5px 13px rgba(174,130,21,.09), inset 0 1px 0 rgba(255,255,255,.88);
        font-size: .63rem;
        font-weight: 950;
        letter-spacing: .01em;
        line-height: 1;
      }
      .little-roar-page .lr-cart-offer-status {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding-left: 7px;
        border-left: 1px solid rgba(118,84,11,.18);
        color: var(--forest);
      }
      .little-roar-page .lr-cart-offer-status::before {
        content: '✓';
        display: grid;
        width: 14px;
        height: 14px;
        place-items: center;
        border-radius: 50%;
        background: var(--forest);
        color: #fff;
        font-size: .53rem;
        font-weight: 950;
      }
      .little-roar-page .lr-cart-discount-amount {
        margin-left: auto;
        color: var(--forest);
        font-size: .82rem;
        font-weight: 950;
        letter-spacing: -.01em;
        white-space: nowrap;
      }
      .little-roar-page .lr-cart-total-row {
        margin-top: 1px;
        padding-top: 9px;
        border-top: 1px dashed rgba(21,79,70,.14);
        color: var(--forest);
      }
      .little-roar-page .lr-cart-total-row span {
        font-size: .78rem;
        font-weight: 950;
      }
      .little-roar-page .lr-cart-total-row strong {
        font-size: 1.12rem;
        letter-spacing: -.02em;
      }
      @media (max-width: 520px) {
        .little-roar-page .lr-cart-offer {
          padding: 5px 8px;
          font-size: .60rem;
        }
        .little-roar-page .lr-cart-discount-amount {
          font-size: .78rem;
        }
      }
    `;
    document.head.appendChild(style);
  };

  const copyCartState = (currentRoot, freshRoot) => {
    const copyText = (selector) => {
      const current = currentRoot.querySelector(selector);
      const fresh = freshRoot.querySelector(selector);
      if (current && fresh) current.textContent = fresh.textContent;
    };

    const copyHtml = (selector) => {
      const current = currentRoot.querySelector(selector);
      const fresh = freshRoot.querySelector(selector);
      if (current && fresh) current.innerHTML = fresh.innerHTML;
    };

    const copyVisibility = (selector) => {
      const current = currentRoot.querySelector(selector);
      const fresh = freshRoot.querySelector(selector);
      if (current && fresh) current.hidden = fresh.hidden;
    };

    copyText('[data-cart-panel-count]');
    copyHtml('[data-cart-items]');
    copyHtml('[data-cart-footer]');
    copyVisibility('[data-cart-footer]');
    copyVisibility('[data-cart-actions]');

    const currentPicker = currentRoot.querySelector('.lr-picker-card');
    const freshPicker = freshRoot.querySelector('.lr-picker-card');
    if (currentPicker && freshPicker) {
      currentPicker.dataset.cartUnits = freshPicker.dataset.cartUnits || '0';
    }
  };

  const applyPricingPresentation = async (root = document.querySelector(rootSelector)) => {
    if (pricingSyncing || !root) return;
    pricingSyncing = true;

    try {
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!cartResponse.ok) throw new Error(`Cart pricing refresh failed: ${cartResponse.status}`);

      const cart = await cartResponse.json();
      const items = Array.isArray(cart.items) ? cart.items : [];
      const lineEls = Array.from(root.querySelectorAll('.lr-cart-line'));

      lineEls.forEach((line, index) => {
        const item = items[index];
        const priceEl = line.querySelector('.lr-cart-line-total strong');
        if (!item || !priceEl) return;
        const originalLinePrice = Number(item.original_line_price ?? item.line_price ?? item.final_line_price ?? 0);
        priceEl.textContent = formatMoney(originalLinePrice, cart.currency);
      });

      const footer = root.querySelector('[data-cart-footer]');
      const actions = root.querySelector('[data-cart-actions]');
      if (!footer) return;

      if (!items.length) {
        footer.hidden = true;
        if (actions) actions.hidden = true;
        return;
      }

      const existingSubtotalLabel = footer.dataset.lrSubtotalLabel
        || footer.querySelector('.lr-cart-price-row span, :scope > span')?.textContent?.trim()
        || 'Subtotal';
      footer.dataset.lrSubtotalLabel = existingSubtotalLabel;

      const originalTotal = Number(cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price ?? 0);
      const total = Number(cart.total_price ?? originalTotal);
      const discountTotal = Math.max(0, originalTotal - total);
      const hasOffer = discountTotal > 0;

      ensurePricingStyles();
      ['width', 'min-width', 'max-width', 'grid-column', 'justify-self', 'align-self'].forEach((property) => {
        footer.style.removeProperty(property);
      });
      footer.classList.add('lr-cart-pricing-summary');
      footer.innerHTML = `
        <div class="lr-cart-price-row">
          <span>${escapeHtml(existingSubtotalLabel)}</span>
          <strong data-cart-subtotal>${escapeHtml(formatMoney(originalTotal, cart.currency))}</strong>
        </div>
        ${hasOffer ? `
          <div class="lr-cart-offer-row">
            <div class="lr-cart-offer" data-cart-offer>
              <span>Roar pack offer</span>
              <span class="lr-cart-offer-status">Applied</span>
            </div>
            <strong class="lr-cart-discount-amount" data-cart-discount>-${escapeHtml(formatMoney(discountTotal, cart.currency))}</strong>
          </div>
        ` : ''}
        <div class="lr-cart-price-row lr-cart-total-row">
          <span>Total</span>
          <strong data-cart-total>${escapeHtml(formatMoney(total, cart.currency))}</strong>
        </div>
      `;
      footer.hidden = false;
      if (actions) actions.hidden = false;
    } catch (error) {
      console.warn('[Little Roar] Cart pricing presentation failed.', error);
    } finally {
      pricingSyncing = false;
    }
  };

  const syncInPageCart = async () => {
    if (syncing) return;
    const currentRoot = document.querySelector(rootSelector);
    const sectionId = currentRoot?.dataset?.roarBuySection;
    if (!currentRoot || !sectionId) return;

    syncing = true;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('_lr_cart_v14', String(Date.now()));

      const response = await fetch(url.toString(), {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Cart section refresh failed: ${response.status}`);

      const holder = document.createElement('div');
      holder.innerHTML = await response.text();
      const freshRoot = holder.querySelector(rootSelector);
      if (!freshRoot) throw new Error('Updated Little Roar cart section was not returned.');

      copyCartState(currentRoot, freshRoot);
      await applyPricingPresentation(currentRoot);
    } catch (error) {
      console.warn('[Little Roar] In-page cart refresh failed.', error);
    } finally {
      syncing = false;
    }
  };

  const handleCartUpdate = (event) => {
    if (event?.detail?.source !== 'little-roar') return;
    syncInPageCart();
  };

  document.addEventListener('cart:updated', handleCartUpdate);
  document.documentElement.addEventListener('cart:update', handleCartUpdate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyPricingPresentation(), { once: true });
  } else {
    applyPricingPresentation();
  }
})();
